import React, { useEffect, useState } from "react";
import { X, Shield, Users, Globe, Lock, AlertCircle } from "lucide-react";

const PermissionModal = ({
  open,
  onClose,
  permissions,
  loading,
  onLoad,
  onUpdate,
}) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState("");

  if (!open) return null;

  const accessLevels = [
    {
      value: "browse_all",
      label: "전체 탐색",
      description: "모든 폴더 접근 가능",
      icon: Globe,
      color: "text-green-600",
    },
    {
      value: "invited_only",
      label: "초대된 폴더만",
      description: "초대받은 폴더만 접근",
      icon: Lock,
      color: "text-blue-600",
    },
  ];

  const handleUpdate = async () => {
    if (!selectedUser || !selectedLevel) return;

    await onUpdate(selectedUser.user_id, selectedLevel);
    setSelectedUser(null);
    setSelectedLevel("");
  };

  const getAccessLevelInfo = (level) => {
    return accessLevels.find((l) => l.value === level) || accessLevels[1];
  };

  const isMaster = permissions.some((p) => p.access_level === "browse_all");

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            EDrive 권한 관리
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">로딩 중...</div>
            </div>
          ) : (
            <>
              {!isMaster && (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">
                        권한 수정 불가
                      </p>
                      <p className="text-sm text-yellow-700 mt-1">
                        권한 관리는 워크스페이스 소유자만 가능합니다.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  멤버별 권한 설정
                </h3>

                <div className="space-y-2">
                  {permissions.map((permission) => {
                    const levelInfo = getAccessLevelInfo(
                      permission.access_level
                    );
                    const isOwner = permission.user_name?.includes("소유자");

                    return (
                      <div
                        key={permission.user_id}
                        className={`p-4 border rounded-lg ${
                          selectedUser?.user_id === permission.user_id
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-gray-300"
                        } ${!isMaster || isOwner ? "" : "cursor-pointer"}`}
                        onClick={() => {
                          if (isMaster && !isOwner) {
                            setSelectedUser(permission);
                            setSelectedLevel(permission.access_level);
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                              <span className="text-gray-600 font-medium">
                                {(permission.user_name || "?")[0]}
                              </span>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">
                                  {permission.user_name}
                                </span>
                                {isOwner && (
                                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">
                                    소유자
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500">
                                ID: {permission.user_id}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <levelInfo.icon
                              className={`w-5 h-5 ${levelInfo.color}`}
                            />
                            <div className="text-right">
                              <p className="text-sm font-medium text-gray-900">
                                {levelInfo.label}
                              </p>
                              <p className="text-xs text-gray-500">
                                {levelInfo.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {selectedUser && isMaster && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 mb-3">
                    {selectedUser.user_name}님의 권한 변경
                  </h4>
                  <div className="space-y-2">
                    {accessLevels.map((level) => (
                      <label
                        key={level.value}
                        className="flex items-center p-3 bg-white border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="radio"
                          name="access_level"
                          value={level.value}
                          checked={selectedLevel === level.value}
                          onChange={(e) => setSelectedLevel(e.target.value)}
                          className="mr-3"
                        />
                        <level.icon className={`w-4 h-4 mr-2 ${level.color}`} />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {level.label}
                          </div>
                          <div className="text-sm text-gray-500">
                            {level.description}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <div className="flex justify-end gap-3 mt-4">
                    <button
                      onClick={() => {
                        setSelectedUser(null);
                        setSelectedLevel("");
                      }}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleUpdate}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      변경 적용
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-6 border-t border-gray-200">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-600">
              <p>• 전체 탐색: 모든 폴더와 파일에 접근 가능</p>
              <p>• 초대된 폴더만: 초대받은 폴더와 그 하위 항목만 접근 가능</p>
              <p className="mt-2 font-medium">
                소유자는 항상 전체 접근 권한을 가집니다.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionModal;
